import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe, ChevronDown, CalendarCheck } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export const Header = () => {
  const { t, language, setLanguage } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isServicesActive = location.pathname.startsWith('/services');

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'da', name: 'Dansk' },
    { code: 'pt', name: 'Português' },
  ];

  const closeMobile = () => setMobileMenuOpen(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/90 shadow-sm">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" data-testid="header-logo-link">
            <div className="logo-container">
              <img
                src="https://customer-assets.emergentagent.com/job_settle-denmark/artifacts/scbjg6pa_create-a-minimalist-3d-style-logo-for-a-_TN12f73sSb-P_h61fzuk9g_oHByH8kJQ22T-0R9tkj5ow.jpeg"
                alt="Next Step Denmark Logo"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-7">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors hover:text-sea-blue ${
                location.pathname === '/' ? 'text-sea-blue' : 'text-gray-700'
              }`}
              data-testid="nav-home"
            >
              {t('nav.home')}
            </Link>

            {/* Services dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-sea-blue outline-none ${
                    isServicesActive ? 'text-sea-blue' : 'text-gray-700'
                  }`}
                  data-testid="nav-services-trigger"
                >
                  {t('nav.services')}
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/services" data-testid="nav-services-overview">
                    {t('nav.servicesOverview')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/services/private" data-testid="nav-services-private">
                    {t('nav.forPrivate')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/services/companies" data-testid="nav-services-companies">
                    {t('nav.forCompanies')}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/about"
              className={`text-sm font-medium transition-colors hover:text-sea-blue ${
                location.pathname === '/about' ? 'text-sea-blue' : 'text-gray-700'
              }`}
              data-testid="nav-about"
            >
              {t('nav.about')}
            </Link>

            <Link
              to="/contact"
              className={`text-sm font-medium transition-colors hover:text-sea-blue ${
                location.pathname === '/contact' ? 'text-sea-blue' : 'text-gray-700'
              }`}
              data-testid="nav-contact"
            >
              {t('nav.contact')}
            </Link>

            <Link to="/booking" data-testid="nav-booking">
              <Button
                size="sm"
                className={`bg-bronze hover:bg-bronze/90 text-white gap-2 ${
                  location.pathname === '/booking' ? 'ring-2 ring-bronze/40' : ''
                }`}
              >
                <CalendarCheck className="h-4 w-4" />
                {t('nav.book')}
              </Button>
            </Link>
          </nav>

          {/* Language Selector & Mobile Menu */}
          <div className="flex items-center space-x-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 border-gray-300 text-gray-700 hover:bg-gray-100"
                  data-testid="language-selector"
                >
                  <Globe className="h-4 w-4" />
                  <span className="hidden sm:inline">
                    {languages.find((l) => l.code === language)?.name}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={language === lang.code ? 'bg-sea-blue/10' : ''}
                    data-testid={`language-option-${lang.code}`}
                  >
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile menu button */}
            <button
              className="md:hidden text-gray-700 hover:text-sea-blue transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-1 border-t border-gray-200">
            <Link
              to="/"
              onClick={closeMobile}
              className={`block px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                location.pathname === '/'
                  ? 'bg-sea-blue/10 text-sea-blue'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {t('nav.home')}
            </Link>

            <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              {t('nav.services')}
            </div>
            <Link
              to="/services"
              onClick={closeMobile}
              className={`block px-6 py-2 text-sm font-medium rounded-md transition-colors ${
                location.pathname === '/services'
                  ? 'bg-sea-blue/10 text-sea-blue'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {t('nav.servicesOverview')}
            </Link>
            <Link
              to="/services/private"
              onClick={closeMobile}
              className={`block px-6 py-2 text-sm font-medium rounded-md transition-colors ${
                location.pathname === '/services/private'
                  ? 'bg-sea-blue/10 text-sea-blue'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {t('nav.forPrivate')}
            </Link>
            <Link
              to="/services/companies"
              onClick={closeMobile}
              className={`block px-6 py-2 text-sm font-medium rounded-md transition-colors ${
                location.pathname === '/services/companies'
                  ? 'bg-sea-blue/10 text-sea-blue'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {t('nav.forCompanies')}
            </Link>

            <Link
              to="/about"
              onClick={closeMobile}
              className={`block px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                location.pathname === '/about'
                  ? 'bg-sea-blue/10 text-sea-blue'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {t('nav.about')}
            </Link>
            <Link
              to="/contact"
              onClick={closeMobile}
              className={`block px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                location.pathname === '/contact'
                  ? 'bg-sea-blue/10 text-sea-blue'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {t('nav.contact')}
            </Link>
            <Link
              to="/booking"
              onClick={closeMobile}
              className="block mx-4 mt-2 px-4 py-2 text-sm font-semibold rounded-md bg-bronze text-white text-center hover:bg-bronze/90 transition-colors"
            >
              <CalendarCheck className="inline h-4 w-4 mr-1.5 -mt-0.5" />
              {t('nav.book')}
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};
